/**
 * Vista 2 · Primer acceso.
 *
 * No hay forma de saltarla: el guard del servidor rechaza con
 * `CAMBIO_PASSWORD_REQUERIDO` toda ruta que no sea `/auth/yo`, `/auth/password`
 * o `/auth/sesion` mientras la cuenta tenga contraseña temporal. Esta pantalla
 * solo hace visible esa regla; no es quien la impone.
 */
import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, PasswordInput } from '@mpfn/ui'
import { AuthCard, Callout, FormField } from '@mpfn/patterns'
import { BrandLockup } from '@mpfn/brand'
import { api, ErrorApi } from '../api/cliente.js'

/** El mismo mínimo que aplica `validarFortaleza` en el servidor. */
const MINIMO = 12

export function PrimerAcceso({
  nombre,
  onCambiada,
}: {
  nombre: string
  onCambiada: () => Promise<void>
}) {
  const idActual = useId()
  const idNueva = useId()
  const idRepetida = useId()
  const [actual, setActual] = useState('')
  const [nueva, setNueva] = useState('')
  const [repetida, setRepetida] = useState('')
  const [tocado, setTocado] = useState(false)

  const cambiar = useMutation({
    mutationFn: (cuerpo: { actual: string; nueva: string }) =>
      api.crear<{ cambiada: true }>('/auth/password', cuerpo),
    onSuccess: () => onCambiada(),
  })

  const corta = nueva.length > 0 && nueva.length < MINIMO
  const distintas = repetida.length > 0 && nueva !== repetida
  const puedeEnviar = actual !== '' && nueva.length >= MINIMO && nueva === repetida

  const mensaje =
    cambiar.error instanceof ErrorApi
      ? cambiar.error.message
      : cambiar.error
        ? 'No fue posible comunicarse con el servidor. Vuelva a intentarlo.'
        : undefined

  return (
    <AuthCard
      aside={
        // Ver la nota de `Acceso.tsx`: la superficie del `AuthCard` es clara.
        <BrandLockup
          tone="onLight"
          size="lg"
          orientation="stacked"
          name="SIGFISC"
          description="Fiscalización de contrataciones"
        />
      }
    >
      <form
        className="sigfisc-formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          setTocado(true)
          if (!puedeEnviar) return
          cambiar.mutate({ actual, nueva })
        }}
      >
        <h1 className="sigfisc-formulario__titulo">Cambie su contraseña</h1>

        <Callout tone="info" title={`Bienvenido, ${nombre}`}>
          Su cuenta se creó con una contraseña temporal. Defina una propia para poder usar el
          sistema; mientras tanto no podrá acceder al resto de las pantallas.
        </Callout>

        {mensaje !== undefined && (
          <Callout tone="critical" title="No se pudo cambiar la contraseña">
            {mensaje}
          </Callout>
        )}

        <FormField id={idActual} label="Contraseña temporal" required>
          <PasswordInput
            autoComplete="current-password"
            autoFocus
            value={actual}
            onChange={(evento) => {
              setActual(evento.target.value)
            }}
          />
        </FormField>

        <FormField
          id={idNueva}
          label="Contraseña nueva"
          required
          hint={`Al menos ${String(MINIMO)} caracteres, y distinta de la temporal.`}
          {...(tocado && corta ? { error: `Use al menos ${String(MINIMO)} caracteres.` } : {})}
        >
          <PasswordInput
            autoComplete="new-password"
            value={nueva}
            onChange={(evento) => {
              setNueva(evento.target.value)
            }}
          />
        </FormField>

        <FormField
          id={idRepetida}
          label="Repita la contraseña nueva"
          required
          {...(tocado && distintas ? { error: 'Las dos contraseñas no coinciden.' } : {})}
        >
          <PasswordInput
            autoComplete="new-password"
            value={repetida}
            onChange={(evento) => {
              setRepetida(evento.target.value)
            }}
            onBlur={() => {
              setTocado(true)
            }}
          />
        </FormField>

        <Button type="submit" variant="primary" size="lg" loading={cambiar.isPending}>
          Guardar y continuar
        </Button>
      </form>
    </AuthCard>
  )
}
